import { MButton } from "@app/components";
import { handleInputChange } from "@app/components/MTextField";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { RowDataCalibrationSpectrometry } from "@app/types/report.types";
import { Autocomplete, Box, TextField } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEfficiencies, useIsotopos } from "@services/servicesGenericsTables";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

const columnGroupingModel = [
  {
    groupId: "Energía baja (< 165.9 Kev)",
    children: [
      { field: "low_energy_TI" },
      { field: "low_energy_x" },
      { field: "low_energy_x2" },
      { field: "low_energy_x3" },
      { field: "low_energy_x4" },
    ],
  },
  {
    groupId: "Energía alta (>= 165.9 Kev)",
    children: [
      { field: "high_energy_TI" },
      { field: "high_energy_x" },
      { field: "high_energy_x2" },
      { field: "high_energy_x3" },
      { field: "high_energy_x4" },
    ],
  },
];

interface GammaFormProps {
  mode: string;
}

const labelsTable = {
  isotope: "Isótopo",
  low_energy_TI: "Termino Independiente",
  low_energy_x: "X",
  low_energy_x2: "X2",
  low_energy_x3: "X3",
  low_energy_x4: "X4",
  id_low: "ID",
  high_energy_TI: "Termino Independiente",
  high_energy_x: "X",
  high_energy_x2: "X2",
  high_energy_x3: "X3",
  high_energy_x4: "X4",
  id_high: "ID",
};

const IGForm = ({ mode }: GammaFormProps) => {
  const isNew = mode === MODES_OPTIONS.NEW;
  const { idCalibration } = useParams();

  const { isotoposList } = useIsotopos();
  const { fechtEfficienciesIsotopes, loadingEfficienciesIsotopes } =
    useEfficiencies();
  const { watch, setValue } = useFormContext();
  const rows = watch("calibrationIG") || [];

  useEffect(() => {
    const fetchData = async () => {
      if (idCalibration) {
        const resp = await fechtEfficienciesIsotopes(idCalibration);
        setValue("calibrationIG", resp);
      }
    };
    if (!isNew) {
      fetchData();
    }else{
      addNewRow();
    }
  }, []);

  const addNewRow = () => {
    const newRow = {
      id: `news-" + ${rows?.length + 1 || 1}`,
      id_low: "",
      id_high: "",
      isotope: null,
      low_energy_TI: "",
      low_energy_x: "",
      low_energy_x2: "",
      low_energy_x3: "",
      low_energy_x4: "",
      high_energy_TI: "",
      high_energy_x: "",
      high_energy_x2: "",
      high_energy_x3: "",
      high_energy_x4: "",
    };
    setValue("calibrationIG", [...rows, newRow]);
  };

  const handleRowUpdate = (
    id: number | string,
    field: keyof RowDataCalibrationSpectrometry,
    value: any
  ) => {
    const updatedRows = rows.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    );

    setValue("calibrationIG", updatedRows);
  };

  const columns: GridColDef[] = [
    ...[
      "low_energy_TI",
      "low_energy_x",
      "low_energy_x2",
      "low_energy_x3",
      "low_energy_x4",
      "high_energy_TI",
      "high_energy_x",
      "high_energy_x2",
      "high_energy_x3",
      "high_energy_x4",
    ].map((field) => ({
      field,
      headerName: labelsTable[field],
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
          }}
        >
          <TextField
            type="text"
            value={params.value || ""}
            onChange={(e) =>
              handleRowUpdate(
                params.id as number,
                field as keyof RowDataCalibrationSpectrometry,
                handleInputChange(e.target.value)
              )
            }
            fullWidth
            sx={{
              "& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input": {
                boxSizing: "inherit",
              },
            }}
          />
        </Box>
      ),
    })),
  ];

  return (
    <>
      <Box mt={2} sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={rows || []}
          columns={columns}
          disableRowSelectionOnClick
          hideFooterPagination
          getRowId={(row) => row.id}
          loading={loadingEfficienciesIsotopes}
          columnGroupingModel={columnGroupingModel}
          experimentalFeatures={{ columnGrouping: true }}
        />
      </Box>
    </>
  );
};

export default IGForm;
